// A1-TILES: every tile in the astrology "What's in this room?" grid routes to a REAL
// designed engine that already lives on this page, or to an honest coming-soon. NEVER a
// raw-data dump, never a silent bounce to My Room (the no-shells law). Shared so the fast
// gate can assert every delivering tile still points at a real config key (a rename that
// breaks a route FAILS THE BUILD), and App.tsx routes from the same one source.
//
// Kinds:
//   chart              -> the real designed birth-chart report (AstrologyReadingModal, isReport)
//   prediction:<key>   -> PredictionModal with the config of that key (server/data/predictions.json)
//   deep:<key>         -> DeepReportModal with the config of that key (server/data/astrology-reports.json)
//   coming-soon        -> honest "coming soon" modal (no engine, no cost, no fake success)
export const ASTROLOGY_TILE_ROUTES = {
  // Natal chart — all three are views of the one real birth-chart report.
  "tropical-birth-chart": { kind: "chart" },
  "planets-houses": { kind: "chart" },
  "ascendant-angles": { kind: "chart" },
  // **A1-PURPOSE (2026-08-10) — "Purpose & Destiny" WAS A DEAD DOOR WAITING ON THE WRONG VENDOR.**
  // Its coming-soon note named `karma_destiny_report`, which is a 405 on the AstrologyAPI plan — a
  // measured refusal, and a true one. What nobody had asked is whether the OTHER vendor could answer
  // it. **AstroAPI's `/analysis/career` returns 200 with 78 real natal interpretations**, on exactly
  // the `{ subject }` shape and exactly the `interpretations[]` response the deep-report engine
  // already reads for the Karmic and Spiritual readings. So there was no engine to build: this tile
  // is one config row (`purpose` in `server/data/astrology-reports.json`) and this one line.
  "career-purpose": { kind: "deep", key: "purpose" },

  // Timing — the two horoscopes are the real predictions engine; transits is the deep
  // Transit report. Moon phases has no designed reading yet, so honest coming-soon.
  "current-transits": { kind: "deep", key: "transit" },
  "moon-phases": { kind: "coming-soon" },
  "daily-astrology": { kind: "prediction", key: "daily" },
  "horoscopes": { kind: "prediction", key: "sign-monthly" },

  // Compatibility — the exact-chart ones are real synastry; the two zodiac-only ones
  // have no designed engine (synastry needs birth details), so honest coming-soon.
  //
  // **`compatibility` IS THE TILE IN THE ROOM ITSELF AND IT WAS MISSING FROM THIS TABLE (2026-08-08,
  // her launch-day report).** So `openOffering` fell through to `setComingSoonTool` and served a
  // not-built popup — **while the engine behind it has been live the whole time.** Its two exact-chart
  // siblings on the very next lines already route here, `/api/astrology/compatibility` is a live route,
  // and `analyzeSynastryWithOpenAI` does real cross-aspect and house-overlay work over both natal
  // charts. Nothing needed building: the room's own door was the one entry nobody added.
  //
  // **THAT IS WHY THIS IS A WIRING AND NOT A REMOVAL** (her rule: wire it if the engine exists, remove
  // it honestly if it does not). It takes the same two-subject picker `synastry` already uses.
  "compatibility": { kind: "deep", key: "synastry" },
  "zodiac-compatibility": { kind: "coming-soon" },
  "love-compatibility": { kind: "deep", key: "synastry" },
  "sun-sign-chemistry": { kind: "coming-soon" },
  "moon-rising-connection": { kind: "deep", key: "synastry" },

  // Deeper systems — Vedic/Indian have raw vendor data but no designed reading, so honest
  // coming-soon rather than a raw dump.
  "indian-astrology": { kind: "coming-soon" },
  "vedic-chart-options": { kind: "coming-soon" },
  "nakshatra-insights": { kind: "coming-soon" },
  "relationship-matching": { kind: "coming-soon" },
};

// The tiles actually shown on the page (the Reports and AI-guidance categories are filtered
// off). A tile NOT listed here still resolves to coming-soon in openOffering (safe default),
// but the gate uses this list to prove the roster is fully accounted for.
export const ASTROLOGY_SHOWN_TILE_IDS = Object.keys(ASTROLOGY_TILE_ROUTES);

export function astrologyTileRoute(tileId) {
  return ASTROLOGY_TILE_ROUTES[tileId] || { kind: "coming-soon" };
}

// R31 — THE OTHER HALF OF THE ONE ROUTING DECISION, AND IT USED TO BE A SECOND HAND-TYPED ROSTER.
//
// `openOffering` reads the table above and opens the reading modal for `kind: "chart"`. Then, 200
// lines away and inside that modal, `isReport` asked a SEPARATE Set (`CHART_TOOL_IDS`, declared in
// App.tsx) whether to render the designed birth-chart report at all. Two rosters, one decision, and
// the receiving side's default is not an error: a tile promoted to `kind: "chart"` here and not added
// there opened the modal, missed `isReport`, and fell through to the generic
// `/api/astrology/room-tool` text path — a PAID vendor call rendering exactly the raw-data dump line
// 2 of this file forbids, with nothing thrown and nothing logged.
//
// So it is DERIVED rather than mirrored (R26: value equality proves two lists agree today, only the
// source proves one still comes from the other). The one member that is not a tile is named here with
// its reason rather than sitting in the list unexplained (R23's lesson).
export const CHART_TOOL_IDS = new Set([
  ...ASTROLOGY_SHOWN_TILE_IDS.filter((id) => ASTROLOGY_TILE_ROUTES[id].kind === "chart"),
  // `birth-chart` is not a tile: it is the capability-card id `activeTool` initialises to. It stays
  // on this list so that if the modal is ever opened from a capability card, it opens on the designed
  // report the card promises and never on the raw text path.
  "birth-chart",
]);
